import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Switch,
  Select,
  Tag,
  Space,
  Typography,
  message,
  Popconfirm,
  Tabs,
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  SafetyOutlined,
} from '@ant-design/icons';
import { appSettingsService, AppSetting } from '../services/appSettingsService';

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const AppSettings: React.FC = () => {
  const [settings, setSettings] = useState<AppSetting[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingSetting, setEditingSetting] = useState<AppSetting | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [form] = Form.useForm();

  useEffect(() => {
    loadSettings();
  }, [activeCategory]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const category = activeCategory === 'all' ? undefined : activeCategory;
      const data = await appSettingsService.getAllSettings(category);
      setSettings(data);
    } catch (error: any) {
      message.error('Failed to load settings: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingSetting(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (setting: AppSetting) => {
    setEditingSetting(setting);
    // Handle null values for price settings
    let displayValue: any = setting.value;
    if ((setting.key === 'marketplace_min_price' || setting.key === 'marketplace_max_price') && (setting.value === null || setting.value === undefined || setting.value === '')) {
      displayValue = '';
    } else if (typeof setting.value === 'boolean') {
      displayValue = setting.value;
    } else {
      displayValue = String(setting.value);
    }
    form.setFieldsValue({
      key: setting.key,
      value: displayValue,
      description: setting.description,
      category: setting.category,
    });
    setModalVisible(true);
  };

  const handleDelete = async (key: string) => {
    try {
      await appSettingsService.deleteSetting(key);
      message.success('Setting deleted successfully');
      loadSettings();
    } catch (error: any) {
      message.error('Failed to delete setting: ' + (error.message || 'Unknown error'));
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      // Handle price settings - convert empty string to null
      let processedValue: any = values.value;
      if (values.key === 'marketplace_min_price' || values.key === 'marketplace_max_price') {
        if (values.value === '' || values.value === null || values.value === undefined) {
          processedValue = null;
        } else {
          processedValue = parseFloat(values.value);
        }
      } else if (values.value === 'true') {
        processedValue = true;
      } else if (values.value === 'false') {
        processedValue = false;
      } else if (!isNaN(Number(values.value)) && values.value !== '') {
        processedValue = Number(values.value);
      }

      if (editingSetting) {
        await appSettingsService.updateSetting(editingSetting.key, {
          ...values,
          value: processedValue,
        });
        message.success('Setting updated successfully');
      } else {
        await appSettingsService.upsertSetting({
          ...values,
          value: processedValue,
        });
        message.success('Setting created successfully');
      }
      
      setModalVisible(false);
      form.resetFields();
      setEditingSetting(null);
      loadSettings();
    } catch (error: any) {
      if (error.errorFields) {
        return;
      }
      message.error('Failed to save setting: ' + (error.message || 'Unknown error'));
    }
  };

  const getCategoryColor = (category: string): string => {
    const colors: Record<string, string> = {
      safety: 'red',
      legal: 'orange',
      marketplace: 'blue',
      general: 'default',
    };
    return colors[category] || 'default';
  };

  const columns = [
    {
      title: 'Key',
      dataIndex: 'key',
      key: 'key',
      render: (key: string) => <code>{key}</code>,
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      render: (value: any, record: AppSetting) => {
        if (record.key === 'marketplace_min_price' || record.key === 'marketplace_max_price') {
          if (value === null || value === undefined || value === '') {
            return <Tag color="default">No limit</Tag>;
          }
          return <span>₹{parseFloat(value).toLocaleString()}</span>;
        }
        if (typeof value === 'boolean') {
          return <Tag color={value ? 'green' : 'red'}>{value ? 'Enabled' : 'Disabled'}</Tag>;
        }
        if (typeof value === 'number') {
          return <span>{value.toLocaleString()}</span>;
        }
        return <span>{String(value)}</span>;
      },
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (category: string) => (
        <Tag color={getCategoryColor(category)}>{category.toUpperCase()}</Tag>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: AppSetting) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this setting?"
            onConfirm={() => handleDelete(record.key)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const safetySettings = settings.filter(s => s.category === 'safety');
  const legalSettings = settings.filter(s => s.category === 'legal');
  const marketplaceSettings = settings.filter(s => s.category === 'marketplace');
  const generalSettings = settings.filter(s => s.category === 'general');

  const tabItems = [
    {
      key: 'all',
      label: 'All Settings',
      children: (
        <Table
          dataSource={settings}
          columns={columns}
          rowKey="_id"
          loading={loading}
        />
      ),
    },
    {
      key: 'safety',
      label: (
        <span>
          <SafetyOutlined /> Safety
        </span>
      ),
      children: (
        <Table
          dataSource={safetySettings}
          columns={columns}
          rowKey="_id"
          loading={loading}
        />
      ),
    },
    {
      key: 'legal',
      label: 'Legal',
      children: (
        <Table
          dataSource={legalSettings}
          columns={columns}
          rowKey="_id"
          loading={loading}
        />
      ),
    },
    {
      key: 'marketplace',
      label: 'Marketplace',
      children: (
        <Table
          dataSource={marketplaceSettings}
          columns={columns}
          rowKey="_id"
          loading={loading}
        />
      ),
    },
    {
      key: 'general',
      label: 'General',
      children: (
        <Table
          dataSource={generalSettings}
          columns={columns}
          rowKey="_id"
          loading={loading}
        />
      ),
    },
  ];

  return (
    <div>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <Title level={2} style={{ margin: 0 }}>App Settings</Title>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
          >
            Create Setting
          </Button>
        </div>

        <Tabs
          activeKey={activeCategory}
          onChange={setActiveCategory}
          items={tabItems}
        />
      </Card>

      <Modal
        title={editingSetting ? 'Edit Setting' : 'Create Setting'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        width={600}
        okText="Save"
        cancelText="Cancel"
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            category: 'general',
          }}
        >
          <Form.Item
            name="key"
            label="Key"
            rules={[{ required: true, message: 'Please enter setting key' }]}
          >
            <Input
              placeholder="e.g., illegal_animal_sale_disclaimer_enabled"
              disabled={!!editingSetting}
            />
          </Form.Item>

          <Form.Item
            name="category"
            label="Category"
            rules={[{ required: true, message: 'Please select category' }]}
          >
            <Select placeholder="Select category">
              <Option value="safety">Safety</Option>
              <Option value="legal">Legal</Option>
              <Option value="marketplace">Marketplace</Option>
              <Option value="general">General</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="value"
            label="Value"
            rules={[
              { 
                required: !editingSetting || (editingSetting.key !== 'marketplace_min_price' && editingSetting.key !== 'marketplace_max_price'),
                message: 'Please enter setting value' 
              }
            ]}
            tooltip="Enter true/false for boolean, number for numeric, or text for string values"
          >
            {editingSetting && (editingSetting.key === 'marketplace_min_price' || editingSetting.key === 'marketplace_max_price') ? (
              <Input
                type="number"
                placeholder="Enter price (leave empty for no limit)"
                min={0}
                step={1}
                addonBefore="₹"
              />
            ) : (
              <Input placeholder="Enter value (true/false, number, or text)" />
            )}
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
          >
            <TextArea
              rows={3}
              placeholder="Describe what this setting does"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AppSettings;

